import React, { ChangeEvent, FormEvent, useEffect, useId, useState } from "react";
import Breadcrumb, { Crumb } from "../../../components/common/Breadcrumbs";
import InputField from "../../../components/common/form/InputField";
import SelectField from "../../../components/common/form/SelectField";
import { useNavigate, useParams } from "react-router-dom";
import {
    useLazyGetCustomerByIdQuery,
    useSearchCustomersQuery,
} from "../../customer/customerApi";
import {
    booleanChecks,
    createOptionListForSelectTag,
    createOptionTypeListFromArray,
    getPrimitiveArray,
    isNullOrEmpty,
    showToast
} from "../../../helpers/utils/Utils";
import { OptionType } from "../../../helpers/utils/StyleUtils";
import { useGetRolesByCustomerIdQuery } from "../roles/rolesApi";
import Form from "../../../components/common/form/Form";
import { handleRTKQuery } from "../../../helpers/utils/RtkQueryUtils";
import {
    useCreateUserMutation, useDeleteUserByIdMutation, useForgotPasswordMutation,
    useGetUserByIdQuery,
    useImpersonateUserMutation,
    useUpdateUserMutation
} from "./usersApi";
import { skipToken } from "@reduxjs/toolkit/query";
import MultiFreeFormField from "../../../components/common/form/MultiFreeFormField";
import { useGetBranchesForCustomerQuery } from "../../customer/branchApi";
import IconButton from "../../../components/common/form/IconButton";
import { FaArrowRightArrowLeft, FaLock } from "react-icons/fa6";
import ResetPassword from "../../../components/common/resetPassword/ResetPassword";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";
import { Routes as AppRoutes } from "../../../routes";
import { FaEnvelope, FaStethoscope, FaTrash } from "react-icons/fa";
import ButtonTray from "../../../components/common/form/ButtonTray";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import { useUtilDataQuery } from "../../profile/profileApi";

interface UserFieldProps {
    crumbs: Crumb[];
    title: string,
    btnText: string,
    isReadOnly?: any
}

const UserForm: React.FC<UserFieldProps> = ({ crumbs, title, btnText, isReadOnly }) => {

    const navigate = useNavigate();
    let { userId } = useParams();

    const [userData, setUserData] = useState<any>({});
    const customersApi = useSearchCustomersQuery({
        pageSize: 10000
    });
    const userByIdApi = useGetUserByIdQuery(userId ?? skipToken);
    const branchesForCustomerApi = useGetBranchesForCustomerQuery({
        pageSize: 100,
        customerId: userData?.customer?.value
    }, { skip: isNullOrEmpty(userData?.customer) });
    const rolesByCustomerIdApi = useGetRolesByCustomerIdQuery(userData?.customer?.value, { skip: isNullOrEmpty(userData?.customer) });
    const [createUser, createUserApiResponse] = useCreateUserMutation();
    const [updateUser, updateUserApiResponse] = useUpdateUserMutation();
    const [impersonateUser, impersonateUserResponse] = useImpersonateUserMutation();
    const [forgotPasswordApi, forgotPasswordApiResponse] = useForgotPasswordMutation()
    const [deleteApi, { isLoading: deleteApiResponse }] = useDeleteUserByIdMutation();
    const [customerApi, customerApiResponse] = useLazyGetCustomerByIdQuery();
    const [customerLevel, setCustomerLevel] = useState('');
    const [checkCustomerLevel, setCheckCustomerLevel] = useState('');
    const { data: utilData } = useUtilDataQuery("");
    useEffect(() => {
        let userObj = userByIdApi?.data;
        if (userObj) {
            setUserData({
                ...userObj?.userDTO,
                enabled: userObj?.userDTO.enabled ? booleanChecks[1] : booleanChecks[0],
                accountNonLocked: userObj?.userDTO.accountNonLocked ? booleanChecks[1] : booleanChecks[0],
                credentialsNonExpired: userObj?.userDTO.credentialsNonExpired ? booleanChecks[1] : booleanChecks[0],
                allowMfa: userObj?.userDTO.allowMfa ? booleanChecks[1] : booleanChecks[0],
                requireMfa: userObj?.userDTO.requireMfa ? booleanChecks[1] : booleanChecks[0],
                customer: userObj?.userDTO.customer.customerName ? {
                    value: userObj?.userDTO.customer?.id,
                    label: userObj?.userDTO.customer?.customerName
                } : null,
                branch: userObj?.userDTO.branch ? {
                    value: userObj?.userDTO.branch?.id,
                    label: userObj?.userDTO.branch?.name
                } : null,
                roles: createOptionTypeListFromArray(userObj?.userDTO.roles),
                team: createOptionTypeListFromArray(userObj.teams),
            })
            setCustomerLevel(userData?.userDTO?.customer?.customerLevel)
        }
    }, [userByIdApi]);


    useEffect(() => {
        if (impersonateUserResponse.isSuccess && !impersonateUserResponse.isLoading) {
            navigate("/home")
            window.location.reload();
        }
    }, [impersonateUserResponse]);

    const handleDeleteSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (userId) {
            await handleRTKQuery(
                () => deleteApi({ userId }).unwrap(),
                (isSuccess: any) => {
                    if (isSuccess) setUserData("")
                    navigate(AppRoutes.Users.path);
                }
            );
        }
    };

    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;
        if (name === "customer") {
            const checkCustomerLevel = customersApi?.data?.data?.find((cur: any) => cur.customerNumber === value)?.customerLevel
            setCheckCustomerLevel(checkCustomerLevel);
        }
        setUserData({
            ...userData,
            [name]: value,
            ...(name === "customer" ? { "branch": null, "roles": null } : {})
        })
    }

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (userData?.roles.length === 0) {
            return showToast("Provide Roles", "warning");
        }
        try {
            const customerResponse = await customerApi(userData?.customer?.value).unwrap();
            const teamArray = getPrimitiveArray(userData?.team, "value");

            
            const commonObj = {
                userDTO: {
                    username: userData?.username,
                    email: userData?.email,
                    customerNumber: userData?.customer?.value,
                    customer: customerResponse,
                    branch: { id: userData?.branch?.value },
                    roles: userData?.roles?.map((el: any) => el.value),
                    team: teamArray,
                    id: userId ?? null,
                },
                teams: teamArray,
            };

            const createOrUpdateUser = async () => {
                if (btnText === "Add") {
                    return await createUser({ ...commonObj, enabled: true }).unwrap();
                } else {
                    const commonObj = {
                        userDTO: {
                            username: userData?.username,
                            email: userData?.email,
                            customerNumber: userData?.customer?.value,
                            customer: customerResponse,
                            branch: {
                                id: userData?.branch?.value,
                                customerId: customerResponse.id,
                            },
                            roles: userData?.roles?.map((el: any) => el.value),
                            team: teamArray,
                            id: userId,
                            enabled: userData?.enabled.value,
                            accountNonLocked: userData?.accountNonLocked.value,
                            credentialsNonExpired: userData?.credentialsNonExpired.value,
                            allowMfa: userData?.allowMfa.value,
                            requireMfa: userData?.requireMfa.value,
                            tenant: userData?.tenant,
                        },
                        teams: teamArray,
                    };
                    return await updateUser({
                        id: userId,
                        requestBody: {
                            ...commonObj,
                        },
                    }).unwrap();
                }
            };
            await handleRTKQuery(createOrUpdateUser, (response: any) => {
                if (response?.success || response?.data?.success) {
                    setUserData({});
                    navigate("/users");
                }
            });

        } catch (error) {
            console.error("Error during handleSubmit:", error);
        }
    };

    const handleImpersonateUserSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const requestParams = {
            username: userData.username
        }
        await impersonateUser(requestParams).unwrap();
    };

    const handleSendResetPasswordSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (userData?.email) {
            return forgotPasswordApi(userData?.email);
        }
    }

    return (

        <div className="px-4 py-2">
            <div className={"justify-between sm:block md:flex lg:flex"}>
                <Breadcrumb crumbs={crumbs} />
                <div className="flex gap-3 items-end">
                    {userId && (
                        <>
                            <ButtonTray
                                buttons={[
                                    {
                                        buttonProps: {
                                            modal: (
                                                <ConfirmationModal
                                                    show={true}
                                                    setShow={() => { }} // Ideally, use useState for modal visibility
                                                    title="Impersonate"
                                                    bodyMessage="Are you sure you want to impersonate this user?"
                                                    onSubmit={handleImpersonateUserSubmit}
                                                    isLoading={impersonateUserResponse.isLoading}
                                                />
                                            ),
                                            onOpen: () => { },
                                            triggerButton: (
                                                <IconButton
                                                    className="h-[35px] min-w-[52px] rounded-[11px]"
                                                    icon={<FaArrowRightArrowLeft />}
                                                    label="Impersonate"
                                                    title="Impersonate"
                                                />
                                            ),
                                            isVisible: hasPermission(["IMPRUSR-V"]) && !utilData?.impersonateId,
                                        },
                                        buttonType: "modal",
                                    },
                                    {
                                        buttonProps: {
                                            modal: (
                                                <ConfirmationModal
                                                    show={true}
                                                    setShow={() => { }}
                                                    title="Send Reset Password"
                                                    bodyMessage="Are you sure you want to send a reset password email?"
                                                    onSubmit={handleSendResetPasswordSubmit}
                                                    isLoading={forgotPasswordApiResponse.isLoading}
                                                />
                                            ),
                                            onOpen: () => { },
                                            triggerButton: (
                                                <IconButton
                                                    className="h-[35px] min-w-[52px] rounded-[11px]"
                                                    icon={<FaEnvelope />}
                                                    label="Send Reset Password"
                                                    title="Send Reset Password"
                                                />
                                            ),
                                            isVisible: hasPermission(["RESETPW-V"]),
                                        },
                                        buttonType: "modal",
                                    },
                                    {
                                        buttonProps: {
                                            modal: (
                                                <ResetPassword
                                                    show={true}
                                                    setShow={() => { }}
                                                    page="User"
                                                />
                                            ),
                                            onOpen: () => { },
                                            triggerButton: (
                                                <IconButton
                                                    className="h-[35px] min-w-[52px] rounded-[11px]"
                                                    icon={<FaLock />}
                                                    label="Reset Password"
                                                    title="Reset Password"
                                                />
                                            ),
                                            isVisible: hasPermission(["RESPASS-V"]),
                                        },
                                        buttonType: "modal",
                                    },
                                    {
                                        buttonProps: {
                                            modal: (
                                                <ConfirmationModal
                                                    show={true}
                                                    setShow={() => { }}
                                                    title="Delete User"
                                                    bodyMessage="Are you sure you want to delete this user?"
                                                    onSubmit={handleDeleteSubmit}
                                                    isLoading={deleteApiResponse}
                                                />
                                            ),
                                            onOpen: () => { },
                                            triggerButton: (
                                                <IconButton
                                                    className="min-w-[52px] rounded-[11px]"
                                                    icon={<FaTrash />}
                                                    title="Delete User"
                                                    hasBackground={true}
                                                />
                                            ),
                                            isVisible: hasPermission(["USR-D"]),
                                        },
                                        buttonType: "modal",
                                    },
                                ]}
                                align="end"
                                gap={3}
                            />
                            {hasPermission(["AUDIT-V"]) &&
                                <AuditableInfoButton obj={userData} className="h-[35px] min-w-[52px] rounded-[11px]" />
                            }
                        </>
                    )}


                </div>
            </div>

            <div className="add_user_outer flex justify-center">
                <div className="adduser_content mt-5 bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md text-sm shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                    <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">{title}</h3>
                    <Form onSubmit={handleSubmit} fields={<>
                        <div className="grid grid-cols-12 adduser_Wrapper">
                            <InputField
                                name={"username"}
                                label="Name"
                                onChange={handleOnChange}
                                value={userData?.username || ""}
                                type="text"
                                placeholder="Enter Name..."
                                required={true}
                                disabled={userId !== undefined}
                            />
                            <InputField
                                label="Email"
                                name={"email"}
                                onChange={handleOnChange}
                                value={userData?.email || ""}
                                type="email"
                                placeholder="Enter Email..."
                                required={true}
                                className={"mt-3"}
                                disabled={isReadOnly}
                            />
                            <SelectField
                                placeholder='Select Customer...'
                                value={userData?.customer || null}
                                options={createOptionListForSelectTag(customersApi?.data?.data, "customerName", "id", "customerNumber")}
                                onChange={(selectedOption) => {
                                    handleOnChange({ name: "customer", value: selectedOption })
                                }}
                                required={true}
                                isLoading={customersApi.isLoading}
                                label={"Customer"}
                                isDisabled={isReadOnly}

                            />
                            <SelectField
                                placeholder='Select Branch...'
                                options={createOptionListForSelectTag(branchesForCustomerApi?.data?.data, "name", "id", "", "displayLabel")}
                                value={userData?.branch || null}
                                onChange={(selectedOption) => {
                                    handleOnChange({ name: "branch", value: selectedOption })
                                }}
                                required={true}
                                isLoading={isNullOrEmpty(userData.customer)}
                                label={"Branch"}
                                isDisabled={isReadOnly}
                            />

                            <SelectField
                                placeholder='Select Roles...'
                                options={createOptionListForSelectTag(rolesByCustomerIdApi.data, "name", "name")}
                                value={userData?.roles || null}
                                onChange={(selectedOption) => {
                                    handleOnChange({ name: "roles", value: selectedOption })
                                }}
                                required={true}
                                isLoading={isNullOrEmpty(userData.customer)}
                                label={"Roles"}
                                isMulti={true}
                                isDisabled={isReadOnly || !userData?.customer}
                            />
                            
                            {btnText !== "Add" && utilData !== null && utilData !== undefined && !(utilData?.customerLevel === "CUSTOMER" && utilData?.parentCustomer === false) &&
                                <MultiFreeFormField
                                    defaultOptions={userData?.team}
                                    placeholder={"Enter Teams..."}
                                    onChange={() => {
                                    }}
                                    name={"teams"}
                                    label={"Teams"}
                                    disabled={true}
                                />
                            }
                            {
                                btnText === "Update" &&
                                <>

                                    <SelectField
                                        placeholder='Select Status...'
                                        options={booleanChecks}
                                        value={userData?.enabled || null}
                                        onChange={(selectedOption) => {
                                            handleOnChange({ name: "enabled", value: selectedOption })
                                        }}
                                        required={true}
                                        isLoading={false}
                                        label={"Status"}
                                        isDisabled={isReadOnly}
                                    />
                                    <SelectField
                                        placeholder='Select Unlocked...'
                                        options={booleanChecks}
                                        value={userData?.accountNonLocked || null}
                                        onChange={(selectedOption) => {
                                            handleOnChange({ name: "accountNonLocked", value: selectedOption })
                                        }}
                                        required={true}
                                        isLoading={false}
                                        label={"Unlocked"}
                                        isDisabled={isReadOnly}
                                    />
                                    <SelectField
                                        placeholder='Select Password Unexpired...'
                                        options={booleanChecks}
                                        value={userData?.credentialsNonExpired || null}
                                        onChange={(selectedOption) => {
                                            handleOnChange({ name: "credentialsNonExpired", value: selectedOption })
                                        }}
                                        required={true}
                                        isLoading={false}
                                        label={"Password Unexpired"}
                                        isDisabled={isReadOnly}
                                    />
                                    <SelectField
                                        placeholder='Select Allow MFA...'
                                        options={booleanChecks}
                                        value={userData?.allowMfa || null}
                                        onChange={(selectedOption) => {
                                            handleOnChange({ name: "allowMfa", value: selectedOption })
                                        }}
                                        required={true}
                                        isLoading={false}
                                        label={"Allow MFA"}
                                        isDisabled={isReadOnly}
                                    />
                                    <SelectField
                                        placeholder='Select Required MFA...'
                                        options={booleanChecks}
                                        value={userData?.requireMfa || null}
                                        onChange={(selectedOption) => {
                                            handleOnChange({ name: "requireMfa", value: selectedOption })
                                        }}
                                        required={true}
                                        isLoading={false}
                                        label={"Required MFA"}
                                        isDisabled={isReadOnly}
                                    />
                                </>
                            }


                            {((btnText === 'Add' && checkCustomerLevel === "TENANT") || (btnText === 'Update' && customerLevel === "TENANT")) &&


                                <SelectField
                                    placeholder='Select InTraining...'
                                    options={booleanChecks}
                                    value={userData?.inTraining || null}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "inTraining", value: selectedOption })
                                    }}
                                    required={true}
                                    isLoading={false}
                                    label={"In Training"}
                                    isDisabled={isReadOnly}
                                />

                            }
                        </div>

                        {!isReadOnly &&


                            <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                </div>
                                <div className="relative col-span-9">
                                    <div className={"mt-4 flex"}>
                                        <ButtonTray
                                            buttons={[
                                                {
                                                    buttonProps: {
                                                        btnText: "Cancel",
                                                        onClick: () => navigate(AppRoutes.Users.path),
                                                        type: "close",
                                                    },
                                                    buttonType: "button"
                                                },
                                                {
                                                    buttonProps: {
                                                        btnText: "Submit",
                                                        type: "submit",
                                                        isLoading: (createUserApiResponse.isLoading || updateUserApiResponse.isLoading),

                                                    },
                                                    buttonType: "button",
                                                }
                                            ]}
                                            align="end"
                                            gap={2}
                                        />
                                    </div>
                                </div>
                            </div>
                        }
                    </>} />


                </div>
            </div>

        </div >
    );
};
export default UserForm;
